import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { VitalityAssessment } from "@/types/vitality";
import { Baby, Home, Stethoscope, Sun, Moon, Utensils, Activity, Heart, Zap, Target, Lightbulb } from "lucide-react";

interface ChildVitalityFormProps {
  assessment: VitalityAssessment;
  updateField: (field: keyof VitalityAssessment, value: any) => void;
  parentName: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
}

const CHILD_TABS = [
  { id: "infos-generales", label: "Informations", icon: Baby },
  { id: "environnement", label: "Environnement", icon: Home },
  { id: "medical", label: "Médical", icon: Stethoscope },
  { id: "hygiene", label: "Hygiène", icon: Sun },
  { id: "sommeil", label: "Sommeil", icon: Moon },
  { id: "alimentation", label: "Alimentation", icon: Utensils },
  { id: "digestion", label: "Digestion", icon: Activity },
  { id: "emotions", label: "Émotions", icon: Heart },
  { id: "vitalite", label: "Vitalité", icon: Zap },
  { id: "recap", label: "Récap", icon: Target },
  { id: "conseils", label: "Conseils", icon: Lightbulb },
];

const EMOTION_OPTIONS = [
  { id: "anxiete", label: "Anxiété" },
  { id: "peurs", label: "Peurs" },
  { id: "coleres", label: "Colères" },
  { id: "tristesse", label: "Tristesse" },
  { id: "hypersensibilite", label: "Hypersensibilité" },
  { id: "agitation", label: "Agitation" },
  { id: "repli", label: "Repli" },
  { id: "manque_confiance", label: "Manque de confiance" },
];

export const ChildVitalityForm: React.FC<ChildVitalityFormProps> = ({
  assessment,
  updateField,
  parentName,
  currentTab,
  setCurrentTab,
}) => {
  const currentTabIndex = CHILD_TABS.findIndex((tab) => tab.id === currentTab);
  const progressPercentage = ((currentTabIndex + 1) / CHILD_TABS.length) * 100;

  const updateChildEmotions = (emotionId: string, checked: boolean) => {
    const currentEmotions = assessment.child_emotions || {};
    updateField("child_emotions", {
      ...currentEmotions,
      [emotionId]: checked,
    });
  };

  return (
    <div className="space-y-6">
      {/* Progress */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{CHILD_TABS[currentTabIndex]?.label || "Informations"}</span>
              <span>{Math.round(progressPercentage)}%</span>
            </div>
            <Progress value={progressPercentage} />
          </div>
        </CardContent>
      </Card>

      <Tabs value={currentTab} onValueChange={setCurrentTab}>
        {/* Mobile: horizontal scroll tabs */}
        <div className="overflow-x-auto -mx-4 px-4 pb-2 sm:mx-0 sm:px-0">
          <TabsList className="inline-flex w-max sm:grid sm:w-full sm:grid-cols-6 lg:grid-cols-11 gap-1 min-w-full sm:min-w-0">
            {CHILD_TABS.map((tab) => (
              <TabsTrigger 
                key={tab.id} 
                value={tab.id} 
                className="text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap flex-shrink-0"
              >
                <tab.icon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <span className="hidden xs:inline sm:inline">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* 1. Informations générales */}
        <TabsContent value="infos-generales">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Baby className="h-5 w-5 text-primary" />
                1. Informations générales
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="child_name">Prénom de l'enfant *</Label>
                  <Input
                    id="child_name"
                    value={assessment.child_name || ""}
                    onChange={(e) => updateField("child_name", e.target.value)}
                    placeholder="Prénom"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="child_last_name">Nom</Label>
                  <Input
                    id="child_last_name"
                    value={assessment.child_last_name || ""}
                    onChange={(e) => updateField("child_last_name", e.target.value)}
                    placeholder="Nom de famille"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="child_date_of_birth">Date de naissance</Label>
                  <Input
                    id="child_date_of_birth"
                    type="date"
                    value={assessment.child_date_of_birth || ""}
                    onChange={(e) => updateField("child_date_of_birth", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="child_age">Âge</Label>
                  <Input
                    id="child_age"
                    value={assessment.child_age || ""}
                    onChange={(e) => updateField("child_age", e.target.value)}
                    placeholder="Ex: 8 ans"
                  />
                </div>
              </div>

              <div className="p-4 bg-secondary/20 rounded-lg border border-secondary/30 space-y-4">
                <h4 className="font-medium text-primary">Parent référent</h4>
                
                <div className="space-y-2">
                  <Label>Nom du parent</Label>
                  <Input
                    value={assessment.parent_name || parentName}
                    onChange={(e) => updateField("parent_name", e.target.value)}
                    placeholder="Nom du parent référent"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="parent_relationship">Lien avec l'enfant</Label>
                  <Select
                    value={assessment.parent_relationship || ""}
                    onValueChange={(value) => updateField("parent_relationship", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner le lien" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mere">Mère</SelectItem>
                      <SelectItem value="pere">Père</SelectItem>
                      <SelectItem value="tuteur">Tuteur/Tutrice</SelectItem>
                      <SelectItem value="grand_parent">Grand-parent</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="parent_phone">Téléphone</Label>
                    <Input
                      id="parent_phone"
                      value={assessment.parent_phone || ""}
                      onChange={(e) => updateField("parent_phone", e.target.value)}
                      placeholder="Téléphone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="parent_email">Email</Label>
                    <Input
                      id="parent_email"
                      type="email"
                      value={assessment.parent_email || ""}
                      onChange={(e) => updateField("parent_email", e.target.value)}
                      placeholder="Email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="parent_address">Adresse</Label>
                  <Input
                    id="parent_address"
                    value={assessment.parent_address || ""}
                    onChange={(e) => updateField("parent_address", e.target.value)}
                    placeholder="Adresse complète"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="consultation_reason">Motif de la consultation *</Label>
                <Textarea
                  id="consultation_reason"
                  rows={4}
                  value={assessment.consultation_reason || ""}
                  onChange={(e) => updateField("consultation_reason", e.target.value)}
                  placeholder="Décrivez le motif principal de cette consultation pour l'enfant..."
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 2. Environnement familial et scolaire */}
        <TabsContent value="environnement">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="h-5 w-5 text-primary" />
                2. Environnement familial et scolaire
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="child_family_situation">Situation familiale</Label>
                <Textarea
                  id="child_family_situation"
                  rows={3}
                  value={assessment.child_family_situation || ""}
                  onChange={(e) => updateField("child_family_situation", e.target.value)}
                  placeholder="Parents ensemble / séparés, fratrie, garde alternée..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_recent_changes">Changements récents</Label>
                <Textarea
                  id="child_recent_changes"
                  rows={3}
                  value={assessment.child_recent_changes || ""}
                  onChange={(e) => updateField("child_recent_changes", e.target.value)}
                  placeholder="Déménagement, séparation, arrivée d'un bébé, deuil..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_school_info">Scolarité</Label>
                <Textarea
                  id="child_school_info"
                  rows={4}
                  value={assessment.child_school_info || ""}
                  onChange={(e) => updateField("child_school_info", e.target.value)}
                  placeholder="Niveau scolaire, ressenti à l'école, relations avec les autres enfants et les enseignants..."
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 3. Historique médical */}
        <TabsContent value="medical">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Stethoscope className="h-5 w-5 text-primary" />
                3. Historique médical
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                <h4 className="font-medium">Naissance</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="birth_voie_basse"
                      checked={assessment.child_birth_type === "voie_basse"}
                      onCheckedChange={(checked) =>
                        updateField("child_birth_type", checked ? "voie_basse" : "")
                      }
                    />
                    <Label htmlFor="birth_voie_basse">Voie basse</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="birth_cesarienne"
                      checked={assessment.child_birth_type === "cesarienne"}
                      onCheckedChange={(checked) =>
                        updateField("child_birth_type", checked ? "cesarienne" : "")
                      }
                    />
                    <Label htmlFor="birth_cesarienne">Césarienne</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="feeding_allaitement"
                      checked={assessment.child_feeding_type === "allaitement"}
                      onCheckedChange={(checked) =>
                        updateField("child_feeding_type", checked ? "allaitement" : "")
                      }
                    />
                    <Label htmlFor="feeding_allaitement">Allaitement</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="feeding_biberon"
                      checked={assessment.child_feeding_type === "biberon"}
                      onCheckedChange={(checked) =>
                        updateField("child_feeding_type", checked ? "biberon" : "")
                      }
                    />
                    <Label htmlFor="feeding_biberon">Biberon</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_pregnancy_notes">Grossesse / naissance particulière</Label>
                <Textarea
                  id="child_pregnancy_notes"
                  rows={3}
                  value={assessment.child_pregnancy_notes || ""}
                  onChange={(e) => updateField("child_pregnancy_notes", e.target.value)}
                  placeholder="Prématurité, complications, réanimation..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_medical_history">Antécédents médicaux notables</Label>
                <Textarea
                  id="child_medical_history"
                  rows={3}
                  value={assessment.child_medical_history || ""}
                  onChange={(e) => updateField("child_medical_history", e.target.value)}
                  placeholder="Maladies infantiles, hospitalisations, allergies..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_current_treatments">Traitements médicaux en cours</Label>
                <Textarea
                  id="child_current_treatments"
                  rows={2}
                  value={assessment.child_current_treatments || ""}
                  onChange={(e) => updateField("child_current_treatments", e.target.value)}
                  placeholder="Médicaments, suivis spécialisés..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_antibiotics_history">Antibiotiques (fréquence / récents)</Label>
                <Textarea
                  id="child_antibiotics_history"
                  rows={2}
                  value={assessment.child_antibiotics_history || ""}
                  onChange={(e) => updateField("child_antibiotics_history", e.target.value)}
                  placeholder="Fréquence, dernière prise..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_medical_notes">Notes complémentaires</Label>
                <Textarea
                  id="child_medical_notes"
                  rows={2}
                  value={assessment.child_medical_notes || ""}
                  onChange={(e) => updateField("child_medical_notes", e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 4. Hygiène de vie */}
        <TabsContent value="hygiene">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sun className="h-5 w-5 text-primary" />
                4. Hygiène de vie
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="child_typical_day">Journée type de l'enfant</Label>
                <Textarea
                  id="child_typical_day"
                  rows={4}
                  value={assessment.child_typical_day || ""}
                  onChange={(e) => updateField("child_typical_day", e.target.value)}
                  placeholder="Déroulement d'une journée typique..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_hobbies">Loisirs</Label>
                <Textarea
                  id="child_hobbies"
                  rows={2}
                  value={assessment.child_hobbies || ""}
                  onChange={(e) => updateField("child_hobbies", e.target.value)}
                  placeholder="Activités extra-scolaires, centres d'intérêt..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_physical_activity">Activité physique / jeux</Label>
                <Textarea
                  id="child_physical_activity"
                  rows={2}
                  value={assessment.child_physical_activity || ""}
                  onChange={(e) => updateField("child_physical_activity", e.target.value)}
                  placeholder="Sport, jeux extérieurs, fréquence..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_screen_time">Temps d'écran (approximatif)</Label>
                <Input
                  id="child_screen_time"
                  value={assessment.child_screen_time || ""}
                  onChange={(e) => updateField("child_screen_time", e.target.value)}
                  placeholder="Ex: 1h par jour, 2h le week-end..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_hygiene_notes">Notes</Label>
                <Textarea
                  id="child_hygiene_notes"
                  rows={2}
                  value={assessment.child_hygiene_notes || ""}
                  onChange={(e) => updateField("child_hygiene_notes", e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 5. Sommeil */}
        <TabsContent value="sommeil">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Moon className="h-5 w-5 text-primary" />
                5. Sommeil
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="child_sleep_schedule">Heure de coucher / lever</Label>
                <Input
                  id="child_sleep_schedule"
                  value={assessment.child_sleep_schedule || ""}
                  onChange={(e) => updateField("child_sleep_schedule", e.target.value)}
                  placeholder="Ex: Coucher 20h30, lever 7h"
                />
              </div>

              <div className="space-y-2">
                <Label>Endormissement</Label>
                <div className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="sleep_easy"
                      checked={assessment.child_falling_asleep === "facile"}
                      onCheckedChange={(checked) =>
                        updateField("child_falling_asleep", checked ? "facile" : "")
                      }
                    />
                    <Label htmlFor="sleep_easy">Facile</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="sleep_difficult"
                      checked={assessment.child_falling_asleep === "difficile"}
                      onCheckedChange={(checked) =>
                        updateField("child_falling_asleep", checked ? "difficile" : "")
                      }
                    />
                    <Label htmlFor="sleep_difficult">Difficile</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_night_wakings">Réveils nocturnes / cauchemars</Label>
                <Textarea
                  id="child_night_wakings"
                  rows={2}
                  value={assessment.child_night_wakings || ""}
                  onChange={(e) => updateField("child_night_wakings", e.target.value)}
                  placeholder="Fréquence, type de réveils..."
                />
              </div>

              <div className="space-y-2">
                <Label>Sommeil réparateur</Label>
                <div className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="restful_yes"
                      checked={assessment.child_restful_sleep === "oui"}
                      onCheckedChange={(checked) =>
                        updateField("child_restful_sleep", checked ? "oui" : "")
                      }
                    />
                    <Label htmlFor="restful_yes">Oui</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="restful_no"
                      checked={assessment.child_restful_sleep === "non"}
                      onCheckedChange={(checked) =>
                        updateField("child_restful_sleep", checked ? "non" : "")
                      }
                    />
                    <Label htmlFor="restful_no">Non</Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 6. Alimentation */}
        <TabsContent value="alimentation">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Utensils className="h-5 w-5 text-primary" />
                6. Alimentation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="child_breakfast">Petit-déjeuner</Label>
                <Textarea
                  id="child_breakfast"
                  rows={2}
                  value={assessment.child_breakfast || ""}
                  onChange={(e) => updateField("child_breakfast", e.target.value)}
                  placeholder="Composition habituelle du petit-déjeuner..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_meals_type">Repas (maison / cantine)</Label>
                <Select
                  value={assessment.child_meals_type || ""}
                  onValueChange={(value) => updateField("child_meals_type", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="maison">Maison</SelectItem>
                    <SelectItem value="cantine">Cantine</SelectItem>
                    <SelectItem value="mixte">Mixte (maison + cantine)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_snacks">Collations / goûters</Label>
                <Textarea
                  id="child_snacks"
                  rows={2}
                  value={assessment.child_snacks || ""}
                  onChange={(e) => updateField("child_snacks", e.target.value)}
                  placeholder="Type de collations habituelles..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_preferred_foods">Aliments appréciés</Label>
                <Textarea
                  id="child_preferred_foods"
                  rows={2}
                  value={assessment.child_preferred_foods || ""}
                  onChange={(e) => updateField("child_preferred_foods", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_refused_foods">Aliments refusés</Label>
                <Textarea
                  id="child_refused_foods"
                  rows={2}
                  value={assessment.child_refused_foods || ""}
                  onChange={(e) => updateField("child_refused_foods", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_drinks">Boissons</Label>
                <Input
                  id="child_drinks"
                  value={assessment.child_drinks || ""}
                  onChange={(e) => updateField("child_drinks", e.target.value)}
                  placeholder="Eau, jus, sodas, quantités..."
                />
              </div>

              {/* Tableau des repas de la semaine */}
              <div className="space-y-3 pt-4 border-t">
                <Label className="text-base font-medium">Menus de la semaine</Label>
                
                {/* Version mobile: cartes empilées */}
                <div className="block sm:hidden space-y-3">
                  {["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"].map((day) => (
                    <div key={day} className="bg-muted/30 rounded-lg p-3 space-y-2">
                      <p className="font-medium capitalize text-primary">{day}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {["matin", "midi", "collation", "soir"].map((meal) => (
                          <div key={meal} className="space-y-1">
                            <Label className="text-[10px] text-muted-foreground capitalize">{meal}</Label>
                            <Input
                              className="h-8 text-xs"
                              value={(assessment.child_weekly_menu?.[day as keyof typeof assessment.child_weekly_menu] as any)?.[meal] || ""}
                              onChange={(e) => {
                                const currentMenu = assessment.child_weekly_menu || {};
                                const currentDay = (currentMenu[day as keyof typeof currentMenu] as any) || {};
                                updateField("child_weekly_menu", {
                                  ...currentMenu,
                                  [day]: {
                                    ...currentDay,
                                    [meal]: e.target.value,
                                  },
                                });
                              }}
                              placeholder="..."
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Version desktop: tableau */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="border p-2 text-left font-medium w-24"></th>
                        <th className="border p-2 text-center font-medium">Matin</th>
                        <th className="border p-2 text-center font-medium">Midi</th>
                        <th className="border p-2 text-center font-medium">Collation</th>
                        <th className="border p-2 text-center font-medium">Soir</th>
                      </tr>
                    </thead>
                    <tbody>
                      {["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"].map((day) => (
                        <tr key={day}>
                          <td className="border p-2 font-medium bg-muted/30 capitalize">{day}</td>
                          {["matin", "midi", "collation", "soir"].map((meal) => (
                            <td key={meal} className="border p-1">
                              <Input
                                className="h-8 text-xs border-0 bg-transparent focus:bg-background"
                                value={(assessment.child_weekly_menu?.[day as keyof typeof assessment.child_weekly_menu] as any)?.[meal] || ""}
                                onChange={(e) => {
                                  const currentMenu = assessment.child_weekly_menu || {};
                                  const currentDay = (currentMenu[day as keyof typeof currentMenu] as any) || {};
                                  updateField("child_weekly_menu", {
                                    ...currentMenu,
                                    [day]: {
                                      ...currentDay,
                                      [meal]: e.target.value,
                                    },
                                  });
                                }}
                                placeholder="..."
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 7. Digestion et élimination */}
        <TabsContent value="digestion">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-primary" />
                7. Digestion et élimination
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Transit</Label>
                <div className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="transit_regular"
                      checked={assessment.child_transit === "regulier"}
                      onCheckedChange={(checked) =>
                        updateField("child_transit", checked ? "regulier" : "")
                      }
                    />
                    <Label htmlFor="transit_regular">Régulier</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="transit_irregular"
                      checked={assessment.child_transit === "irregulier"}
                      onCheckedChange={(checked) =>
                        updateField("child_transit", checked ? "irregulier" : "")
                      }
                    />
                    <Label htmlFor="transit_irregular">Irrégulier</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_digestive_issues">Ballonnements / douleurs abdominales</Label>
                <Textarea
                  id="child_digestive_issues"
                  rows={3}
                  value={assessment.child_digestive_issues || ""}
                  onChange={(e) => updateField("child_digestive_issues", e.target.value)}
                  placeholder="Fréquence, localisation, circonstances..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_digestion_notes">Notes</Label>
                <Textarea
                  id="child_digestion_notes"
                  rows={2}
                  value={assessment.child_digestion_notes || ""}
                  onChange={(e) => updateField("child_digestion_notes", e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 8. Émotions et comportement */}
        <TabsContent value="emotions">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-primary" />
                8. Émotions et comportement
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Label>Émotions / comportements observés</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {EMOTION_OPTIONS.map((emotion) => (
                    <div key={emotion.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`emotion_${emotion.id}`}
                        checked={assessment.child_emotions?.[emotion.id] || false}
                        onCheckedChange={(checked) =>
                          updateChildEmotions(emotion.id, checked as boolean)
                        }
                      />
                      <Label htmlFor={`emotion_${emotion.id}`} className="text-sm">
                        {emotion.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_observed_behaviors">Comportements observés</Label>
                <Textarea
                  id="child_observed_behaviors"
                  rows={3}
                  value={assessment.child_observed_behaviors || ""}
                  onChange={(e) => updateField("child_observed_behaviors", e.target.value)}
                  placeholder="Décrivez les comportements observés par les parents ou l'entourage..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_expressed_emotions">Émotions dominantes exprimées par l'enfant / les parents</Label>
                <Textarea
                  id="child_expressed_emotions"
                  rows={3}
                  value={assessment.child_expressed_emotions || ""}
                  onChange={(e) => updateField("child_expressed_emotions", e.target.value)}
                  placeholder="Ce que l'enfant ou les parents expriment..."
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 9. Vitalité globale */}
        <TabsContent value="vitalite">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                9. Vitalité globale
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Niveau d'énergie perçu</Label>
                <div className="flex gap-4">
                  {[
                    { id: "bas", label: "Bas" },
                    { id: "moyen", label: "Moyen" },
                    { id: "bon", label: "Bon" },
                  ].map((level) => (
                    <div key={level.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`energy_${level.id}`}
                        checked={assessment.child_energy_level === level.id}
                        onCheckedChange={(checked) =>
                          updateField("child_energy_level", checked ? level.id : "")
                        }
                      />
                      <Label htmlFor={`energy_${level.id}`}>{level.label}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Fatigue</Label>
                <div className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="fatigue_yes"
                      checked={assessment.child_fatigue === "oui"}
                      onCheckedChange={(checked) =>
                        updateField("child_fatigue", checked ? "oui" : "")
                      }
                    />
                    <Label htmlFor="fatigue_yes">Oui</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="fatigue_no"
                      checked={assessment.child_fatigue === "non"}
                      onCheckedChange={(checked) =>
                        updateField("child_fatigue", checked ? "non" : "")
                      }
                    />
                    <Label htmlFor="fatigue_no">Non</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_immunity">Immunité (rhumes, otites, infections fréquentes)</Label>
                <Textarea
                  id="child_immunity"
                  rows={3}
                  value={assessment.child_immunity || ""}
                  onChange={(e) => updateField("child_immunity", e.target.value)}
                  placeholder="Fréquence des infections, types..."
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 10. Récapitulatif et axes d'accompagnement */}
        <TabsContent value="recap">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                10. Récapitulatif et axes d'accompagnement
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="child_priorities">Priorités observées</Label>
                <Textarea
                  id="child_priorities"
                  rows={4}
                  value={assessment.child_priorities || ""}
                  onChange={(e) => updateField("child_priorities", e.target.value)}
                  placeholder="Principales problématiques identifiées..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="child_support_axes">Axes d'accompagnement (hygiène de vie, alimentation, émotions)</Label>
                <Textarea
                  id="child_support_axes"
                  rows={4}
                  value={assessment.child_support_axes || ""}
                  onChange={(e) => updateField("child_support_axes", e.target.value)}
                  placeholder="Axes prioritaires d'accompagnement..."
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 11. Conseils proposés */}
        <TabsContent value="conseils">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-primary" />
                11. Conseils proposés
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="child_recommendations">Conseils et recommandations</Label>
                <Textarea
                  id="child_recommendations"
                  rows={10}
                  value={assessment.child_recommendations || ""}
                  onChange={(e) => updateField("child_recommendations", e.target.value)}
                  placeholder="Détaillez ici tous les conseils proposés pour l'enfant : hygiène de vie, alimentation, compléments, techniques naturelles..."
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export const CHILD_VITALITY_TABS = CHILD_TABS;
